import { z } from "zod"

import { ethereumAddress } from "../base"

export const individualPairSchema = z.object({
  id: z.number(),
  address: z.string(),
  apr: z.string(),
  decimals: z.number(),
  displayName: z.string(),
  displayType: z.string(),
  extendedType: z.null(),
  gauge: z.object({
    address: z.string().nullable(),
    apr: z.string(),
    bribeAddress: z.string().nullable(),
    incentives: z.record(
      z.string(),
      z.object({
        symbol: z.string(),
        priceUsd: z.number(),
        rewardAmount: z.number(),
        rewardAmountUsd: z.number(),
      }),
    ),
    reserve0: z.string(),
    reserve1: z.string(),
    rewardRate: z.string(),
    totalBribeValueUsd: z.string(),
    totalSupply: z.string(),
    votes: z.string(),
    votesPercent: z.string(),
  }),
  token0: z.object({
    address: ethereumAddress,
    symbol: z.string(),
    decimals: z.number(),
  }),
  token1: z.object({
    address: ethereumAddress,
    symbol: z.string(),
    decimals: z.number(),
  }),
  stable: z.boolean(),
  symbol: z.string(),
  tags: z.array(z.string()),
  tvlUsd: z.string(),
})

export type IndividualPairResponse = z.infer<typeof individualPairSchema>

const minimalPairItemSchema = z.object({
  id: z.number(),
  displayName: z.string().nullable(),
  displayType: z.string(),
  extendedType: z.string().nullable(),
  address: ethereumAddress,
  decimals: z.number(),
  isStable: z.boolean(),
  token0: z.object({
    symbol: z.string(),
    address: ethereumAddress,
    decimals: z.number(),
  }),
  token1: z.object({
    symbol: z.string(),
    address: ethereumAddress,
    decimals: z.number(),
  }),
  gauge: z
    .object({
      address: ethereumAddress,
    })
    .nullable(),
})

export const minimalPairsResponseSchema = z.record(z.string(), minimalPairItemSchema)

export type MinimalPairResponse = z.infer<typeof minimalPairsResponseSchema>
export type MinimalPairResponseItem = MinimalPairResponse[number]

const scaleMinimalPairItemSchema = z.object({
  id: z.number(),
  address: ethereumAddress,
  apr: z.number(),
  aprLastEpoch: z.number(),
  decimals: z.number(),
  displayName: z.string().nullable(),
  displayType: z.string(),
  extendedType: z.string().nullable(),
  fee: z.number(),
  gauge: z
    .object({
      address: ethereumAddress,
    })
    .optional(),
  hasGauge: z.boolean(),
  isStable: z.boolean(),
  reserve0: z.string(),
  reserve1: z.string(),
  tags: z.array(z.string()),
  totalSupply: z.string(),
  tvlUsd: z.number(),
  token0: z.object({
    symbol: z.string(),
    address: ethereumAddress,
    decimals: z.number(),
  }),
  token1: z.object({
    symbol: z.string(),
    address: ethereumAddress,
    decimals: z.number(),
  }),
})

export const scaleMinimalPairsResponseSchema = z.array(scaleMinimalPairItemSchema)

export type ScaleMinimalPairResponse = z.infer<typeof scaleMinimalPairsResponseSchema>
export type ScaleMinimalPairResponseItem = ScaleMinimalPairResponse[number]

const pairItem = z.object({
  id: z.number(),
  name: z.string(),
  symbol: z.string(),
  displayName: z.string(),
  displayType: z.string(),
  extendedType: z.string().nullable(),
  address: ethereumAddress,
  chainId: z.number(),
  decimals: z.number(),
  isStable: z.boolean(),
  apr: z.string(),
  aprLastEpoch: z.string(),
  aprIncoming: z.string(),
  totalSupply: z.string(),
  tvlUsd: z.string(),
  tags: z.array(z.string()),
  token0: z.object({
    symbol: z.string(),
    address: ethereumAddress,
    decimals: z.number(),
  }),
  token1: z.object({
    symbol: z.string(),
    address: ethereumAddress,
    decimals: z.number(),
  }),
  lastActiveTimestamp: z.number(),
  tvl: z.string(),
  fee: z.string(),
})

export const pairResponseSchema = z.record(z.string(), pairItem)

export type PairResponse = z.infer<typeof pairResponseSchema>
export type PairResponseItem = PairResponse[number]
